import {
  NewsfeedFieldType,
  AdvancedNewsManager,
  Story,
  StoryObject,
  filterDuplicateArticles,
} from '@benzinga/advanced-news-manager';
import { safeAwaitAll, SafeError, SafePromise, safeTimeout, SafeType } from '@benzinga/safe-await';
import { Session, Expression, ExpressionObject } from '@benzinga/session';
import { SimpleNewsQuery, SimpleNewsOptions, TidParam } from './entities';
import { DateTime } from 'luxon';
import { ListenableSubscribable } from '@benzinga/subscribable';
import {
  generateChannelsAndTagsGroupExpressionObject,
  generateChannelsQueryExpressionObject,
  generateSearchQueryExpressionObject,
  generateTagsQueryExpressionObject,
  generateTickersQueryExpressionObject,
  generateUserIdQueryExpressionObject,
} from './generators';
import { authorTranslatedStoryTypes, BENZINGA_INSIGHTS, PARTNER_CONTENT } from './constants';

interface InternalNewsRequestErrorEvent {
  error: SafeError;
  errorType:
    | 'news:get_content_types_error'
    | 'news:get_story_error'
    | 'news:get_stories_error'
    | 'news:simply_query_news_error';
  type: 'error';
}

export type InternalNewsRequestEvent = InternalNewsRequestErrorEvent;

export type InternalNewsRequestSubscription = ReturnType<InternalNewsRequest['subscribe']>;

export class InternalNewsRequest extends ListenableSubscribable<InternalNewsRequestEvent> {
  session: Session;
  constructor(session: Session) {
    super();
    this.session = session;
  }

  getStoriesWithTickers = async (
    tickers: string | string[],
    options?: SimpleNewsOptions,
  ): SafePromise<StoryObject[]> => {
    const expression = generateTickersQueryExpressionObject(tickers);
    return this.getStories(expression, options);
  };

  getStoriesWithTags = async (tags: TidParam, options?: SimpleNewsOptions): SafePromise<StoryObject[]> => {
    const expression = generateTagsQueryExpressionObject(tags);
    return this.getStories(expression, options);
  };

  getStoriesWithChannels = async (channels: TidParam, options?: SimpleNewsOptions): SafePromise<StoryObject[]> => {
    const expression = generateChannelsQueryExpressionObject(channels);
    return this.getStories(expression, options);
  };

  getStoriesWithChannelsAndTags = async (
    channels: TidParam,
    tags: TidParam,
    options?: SimpleNewsOptions,
    operator: 'and' | 'or' = 'and',
  ): SafePromise<StoryObject[]> => {
    const expression = generateChannelsAndTagsGroupExpressionObject(channels, tags, operator);
    return this.getStories(expression, options);
  };

  getStoriesWithIds = async (ids: number[]): SafePromise<StoryObject[]> => {
    const reqs = ids.map((id: number) => {
      return this.session.getManager(AdvancedNewsManager).getStory(id);
    });

    const response = await safeAwaitAll(reqs);

    const nodes: StoryObject[] =
      response?.ok?.map(story => {
        return story.toObject() as StoryObject;
      }) ?? [];

    if (response.err) {
      console.error(response.err);
      return { ok: [] };
    } else {
      return {
        ok: filterDuplicateArticles(nodes),
      };
    }
  };

  queryTopStories = async (
    query: ExpressionObject<NewsfeedFieldType>,
    options?: SimpleNewsOptions,
  ): SafePromise<StoryObject[]> => {
    const now = DateTime.now();
    return this.getStories(
      {
        group: {
          lhs: {
            group: {
              lhs: {
                expression: ['Channels.tid', 'any', [138079]],
                operator: 'expression',
              },
              operator: 'and',
              rhs: {
                expression: ['CreatedAt', 'gt', now.minus({ days: 2 }).toFormat('yyyy-MM-dd')],
                operator: 'expression',
              },
            },
            operator: 'group',
          },
          operator: 'and',
          rhs: query,
        },
        operator: 'group',
      },
      options,
    );
  };

  querySearch = async (query: SimpleNewsQuery, options?: SimpleNewsOptions): SafePromise<StoryObject[]> => {
    const expression = generateSearchQueryExpressionObject(query);
    return this.getStories(expression, options);
  };

  simplyQueryNews = async (query: SimpleNewsQuery, options: SimpleNewsOptions): SafePromise<StoryObject[]> => {
    const { author, body, channelAndTagQueryOperator = 'and', channels, search, tags, tickers, title, userId } = query;
    const isSearchQueryNeeded = search || author || title || body || userId;

    let expressionObject: ExpressionObject<NewsfeedFieldType> | null = null;
    if (tags && channels) {
      expressionObject = generateChannelsAndTagsGroupExpressionObject(channels, tags, channelAndTagQueryOperator);
    } else if (channels) {
      expressionObject = generateChannelsQueryExpressionObject(channels);
    } else if (tags) {
      expressionObject = generateTagsQueryExpressionObject(tags);
    } else if (Array.isArray(tickers) && tickers.length) {
      expressionObject = generateTickersQueryExpressionObject(tickers);
    }

    if (isSearchQueryNeeded) {
      const searchQueryExpressionObject = generateSearchQueryExpressionObject(query);
      if (expressionObject) {
        expressionObject = {
          lhs: expressionObject,
          operator: 'and',
          rhs: searchQueryExpressionObject,
        };
      } else {
        expressionObject = searchQueryExpressionObject;
      }
    }

    return await this.getStories(expressionObject, options, query);
  };

  getStories = async (
    expressionObject: ExpressionObject<NewsfeedFieldType> | null,
    options?: SimpleNewsOptions,
    query?: SimpleNewsQuery,
  ): SafePromise<StoryObject[]> => {
    const fields: NewsfeedFieldType[] = [
      'ID',
      'IsBzPost',
      'IsBzProPost',
      'name',
      'assets',
      'CanonicalPath',
      'Channels',
      'CreatedAt',
      'Meta',
      'NodeID',
      'Tickers',
      'Type',
      'Tags',
      'TeaserText',
      'Title',
      'Type',
      'UpdatedAt',
    ];

    // this is a quick hack to ensure our new sponsored post type, benzinga_reach is included
    // const defaultType =
    //   (typeof options?.type === 'string' && options?.type === 'story' && !options?.excludeSponsored) || !options?.type
    //     ? ['story', 'benzinga_reach']
    //     : options?.type ?? 'story';

    // const type = options?.type && Array.isArray(options?.type) ? options?.type : defaultType;
    const type = options?.type && Array.isArray(options?.type) ? options?.type : [options?.type ?? 'story'];

    if (options?.displayOutput === 'full' || query?.search || query?.body) {
      fields.push('Body');
    }

    let contentTypesToExclude = authorTranslatedStoryTypes;

    if (options?.excludeContentTypes && options.excludeContentTypes.length > 0) {
      contentTypesToExclude = contentTypesToExclude.concat(options.excludeContentTypes);
    }

    let where: ExpressionObject<NewsfeedFieldType> = {
      lhs: {
        group: {
          // Exclude Translated Content Types
          expression: options?.allOriginalArticles ? ['Type', 'any', contentTypesToExclude] : ['Type', 'any', type],
          operator: 'expression',
        },
        operator: options?.allOriginalArticles ? 'not' : 'group',
      },
      operator: 'and',
      rhs:
        options?.headlines !== 'include'
          ? ({
              expression: ['IsBzPost', 'eq', options?.headlines ? false : true],
              operator: 'expression',
            } as ExpressionObject<NewsfeedFieldType>)
          : ({} as ExpressionObject<NewsfeedFieldType>),
    };

    if (options?.before) {
      where = {
        lhs: where,
        operator: 'and',
        rhs: {
          expression: ['CreatedAt', 'lt', options.before],
          operator: 'expression',
        },
      };
    }

    if (options?.after) {
      where = {
        lhs: where,
        operator: 'and',
        rhs: {
          expression: ['CreatedAt', 'gt', options.after],
          operator: 'expression',
        },
      };
    }

    if (query?.primaryChannel) {
      where = {
        lhs: where,
        operator: 'and',
        rhs: generateChannelsQueryExpressionObject(query.primaryChannel),
      };
    }

    if (options?.excludeContributed) {
      where = {
        lhs: where,
        operator: 'and',
        rhs: {
          group: {
            expression: ['Meta.Flags.ContributedContent', 'eq', true], // Contributed Content
            operator: 'expression',
          },
          operator: 'not',
        },
      };
    }

    if (options?.excludeAutomated) {
      where = {
        lhs: where,
        operator: 'and',
        rhs: {
          group: generateUserIdQueryExpressionObject([BENZINGA_INSIGHTS]),
          operator: 'not',
        },
      };
    }

    if (options?.excludeSponsored) {
      where = {
        lhs: where,
        operator: 'and',
        rhs: {
          group: generateChannelsAndTagsGroupExpressionObject([PARTNER_CONTENT], [PARTNER_CONTENT], 'or'),
          operator: 'not',
        },
      };
    }

    if (query?.userId) {
      where = {
        lhs: where,
        operator: 'and',
        rhs: generateUserIdQueryExpressionObject(query?.userId),
      };
    }

    if (expressionObject) {
      where = {
        lhs: where,
        operator: 'and',
        rhs: {
          group: expressionObject,
          operator: 'group',
        },
      };
    }

    const request = this.session.getManager(AdvancedNewsManager).getStories({
      fields: fields,
      limit: options?.limit && options?.limit <= 100 ? options?.limit : Math.min(100, options?.limit ?? 4),
      offset: options?.offset ? options?.offset : 0,
      sort: {
        CreatedAt: options?.sortOrderCreated && options?.sortOrderCreated === 'asc' ? 1 : -1,
      },
      where: Expression.fromExpressionType<NewsfeedFieldType>(where),
    });

    const results: SafeType<Story[]> = await safeTimeout(request, 10000);

    if (!results?.err && results.ok) {
      const nodes: StoryObject[] = results?.ok.map(story => {
        return story.toObject() as StoryObject;
      });
      return {
        ok: filterDuplicateArticles(nodes),
      };
    }

    return {
      err: results.err,
    };
  };
}
